import { ApiError } from "@/api/client";
import { useCurrentUser, useOrganization, useSubscriptionStatus } from "@/api/hooks";

import { BrandingCard } from "@/routes/PlanBillingPage";

/** Contractor-side `/settings` page.
 *
 *  Home for everything that affects how the contractor's company
 *  appears to homeowners on quote PDFs and the public quote view:
 *  logo, business contact (phone / email / website / address),
 *  brand color, default quote terms, deposit %, reminder cadence,
 *  email subject + body templates.
 *
 *  Used to live buried inside the BrandingCard on `/billing` —
 *  which (a) made it undiscoverable to first-time contractors and
 *  (b) disappeared entirely on comped pilot orgs (which hide most
 *  of the billing page). This route is independent of plan_tier so
 *  it works the same on Free, Pro, Crew, and Comped.
 *
 *  The whole page is just a thin wrapper around BrandingCard,
 *  which carries all the form state + submission. We load the
 *  org + subscription-status data here and pass them down. */
export function ContractorSettingsPage() {
  const me = useCurrentUser();
  const orgId = me.data?.organization_id ?? null;
  const org = useOrganization(orgId ?? undefined);
  const status = useSubscriptionStatus(orgId);

  if (me.isLoading || (orgId && (org.isLoading || status.isLoading))) {
    return <p className="text-sm text-graphite-soft">Loading…</p>;
  }
  if (!me.data) {
    return null;
  }
  if (!orgId) {
    return (
      <div className="space-y-3">
        <h1 className="font-mono text-2xl font-bold tracking-tight">Settings</h1>
        <p className="max-w-prose text-sm text-graphite-soft">
          You don't currently have an organization context. PencilOut staff should pick an org from
          the Organizations page to view its settings.
        </p>
      </div>
    );
  }
  if (status.error) {
    return (
      <p className="text-sm text-red-pencil">
        {status.error instanceof ApiError ? status.error.detail : "Could not load settings."}
      </p>
    );
  }
  if (!status.data) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Settings</h1>
        <p className="mt-1 max-w-prose text-sm text-graphite-soft">
          Your business identity on quote PDFs + the public quote view. Update once; reused on every
          quote you send.
        </p>
      </div>

      <BrandingCard orgId={orgId} org={org.data ?? null} status={status.data} />
    </div>
  );
}
